import type {Editor} from "@tiptap/core";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {TextSelection, type EditorState, type PluginKey, type StateField} from "@tiptap/pm/state";

/** The meta a transaction carries when `openVariablePicker` typed the `/`, or when that is forgotten. */
export const VARIABLE_PICKER_TRIGGER = "variablePickerTrigger";

/**
 * The spec field that marks the trigger plugin `variablePickerPlugins` builds; its value is the key
 * of the suggestion that plugin watches. `openVariablePicker` finds every popup of an editor by it.
 */
export const VARIABLE_PICKER_SUGGESTION_KEY = "variablePickerSuggestionKey";

const FORGET = "forget";

/** The `/` the toolbar typed, where it is now, and how far the query after it reaches. */
export interface VariablePickerTrigger {
    /** Where the inserted text starts: the space when one went in first, else the `/`. */
    from: number;
    slash: number;
    /** The end of the query the popup last showed for this `/`. */
    to: number;
    /** The popup has been open on this `/`; only then does its closing take the `/` out again. */
    opened: boolean;
}

/** The suggestion plugin's state, as far as the trigger needs it. */
interface SuggestionState {
    active: boolean;
    range: {from: number; to: number};
}

/** One popup this editor can open: the toolbar `/` it holds, if any, and its suggestion's state. */
interface PickerSession {
    trigger: VariablePickerTrigger | null;
    suggestion: SuggestionState | undefined;
}

const pickerSessions = (state: EditorState): PickerSession[] =>
    state.plugins.flatMap(plugin => {
        const suggestionKey = plugin.spec[VARIABLE_PICKER_SUGGESTION_KEY] as PluginKey | undefined;
        if (!suggestionKey) return [];

        return [{
            trigger: plugin.getState(state) as VariablePickerTrigger | null,
            suggestion: suggestionKey.getState(state) as SuggestionState | undefined,
        }];
    });

/** Takes the toolbar's `/` and its query out, without history, and forgets it. */
const removeTrigger = (editor: Editor, from: number, to: number) => {
    editor.view.dispatch(
        editor.state.tr.delete(from, to).setMeta(VARIABLE_PICKER_TRIGGER, FORGET).setMeta("addToHistory", false),
    );
};

const holdsTrigger = (doc: ProseMirrorNode, trigger: VariablePickerTrigger) =>
    trigger.from <= trigger.slash
    && trigger.slash < trigger.to
    && trigger.to <= doc.content.size
    && doc.textBetween(trigger.slash, trigger.slash + 1) === "/"
    && (trigger.from === trigger.slash || doc.textBetween(trigger.from, trigger.slash) === " ");

/*
    Kept as plugin state so the positions are mapped through every transaction before any plugin
    view hears of it: by the time the popup's `onExit` runs, the range is already where the `/` is
    now, whatever the closing transaction did to the document.

    The query's end is read from the suggestion's state *before* each transaction and then mapped,
    so a space typed after the query (which closes the popup) is never counted as part of it.
 */
export const variablePickerTriggerState = (suggestionKey: PluginKey): StateField<VariablePickerTrigger | null> => ({
    init: () => null,

    apply: (tr, value, oldState, newState) => {
        const meta = tr.getMeta(VARIABLE_PICKER_TRIGGER) as Omit<VariablePickerTrigger, "opened"> | typeof FORGET | undefined;
        if (meta === FORGET) return null;
        if (meta) return holdsTrigger(newState.doc, {...meta, opened: false}) ? {...meta, opened: false} : null;
        if (!value) return null;

        let next = value;
        const suggestion = suggestionKey.getState(oldState) as SuggestionState | undefined;
        if (suggestion?.active && suggestion.range.from === next.slash) {
            next = {...next, to: suggestion.range.to, opened: true};
        }

        if (tr.docChanged) {
            next = {
                ...next,
                from: tr.mapping.map(next.from, 1),
                slash: tr.mapping.map(next.slash, 1),
                to: tr.mapping.map(next.to, -1),
            };
        }

        return holdsTrigger(newState.doc, next) ? next : null;
    },
});

/**
 * After the popup closed: takes the toolbar's `/` and the query typed after it out again, unless
 * a variable went in their place. Neither the typing of the `/` nor its removal is an undo step.
 */
export function finishVariablePickerTrigger(
    editor: Editor,
    triggerKey: PluginKey<VariablePickerTrigger | null>,
    selected: boolean,
) {
    // The popup closes from inside a view update; the document is changed once that update is done.
    queueMicrotask(() => {
        if (editor.isDestroyed) return;

        const trigger = triggerKey.getState(editor.state);
        if (!trigger) return;

        const tr = editor.state.tr.setMeta(VARIABLE_PICKER_TRIGGER, FORGET).setMeta("addToHistory", false);
        if (trigger.opened && !selected) tr.delete(trigger.from, trigger.to);

        editor.view.dispatch(tr);
    });
}

/**
 * Before a pick replaces the toolbar's `/`: that `/` was never an undo step, so a pick that replaced
 * it would bring it back on undo, with nothing left to take it out. It goes first, without history;
 * the space before it goes back in as an undo step, adjacent to the pick, so one undo removes both.
 * Returns where the pick goes in, as a collapsed range.
 */
export function clearTriggerForPick(editor: Editor, trigger: VariablePickerTrigger, queryEnd: number) {
    removeTrigger(editor, trigger.from, queryEnd);

    if (trigger.from === trigger.slash) return {from: trigger.from, to: trigger.from};

    editor.view.dispatch(editor.state.tr.insertText(" ", trigger.from));
    return {from: trigger.from + 1, to: trigger.from + 1};
}

/**
 * Types the `/` the suggestion listens for. A space goes in first when the caret sits against a
 * word. The `/` is remembered, so closing the popup without a pick takes it out again; a `/` the
 * author typed is never touched.
 *
 * With a popup already open it only focuses the editor. When no suggestion takes the `/` (an empty
 * catalogue, a host's `allow`), it is taken out again at once and the call returns false.
 */
export function openVariablePicker(editor: Editor): boolean {
    if (!editor.isEditable) return false;

    const sessions = pickerSessions(editor.state);
    // No picker in this editor: a `/` without history would only be stuck in the text.
    if (!sessions.length) return false;

    // A second `/` would take the first one's place, leaving that one and its query in the text.
    if (sessions.some(({trigger, suggestion}) => trigger || suggestion?.active)) {
        editor.commands.focus();
        return true;
    }

    const inserted = editor
        .chain()
        .focus()
        .command(({tr, commands}) => {
            // A selected block with no inline content has nowhere to put the `/`.
            if (!tr.selection.$from.parent.inlineContent && !commands.createParagraphNear()) return false;

            // Collapsed to its end first: the `/` goes in without history, so text it replaced could not be undone back.
            if (!tr.selection.empty) tr.setSelection(TextSelection.near(tr.doc.resolve(tr.selection.to)));

            const {from} = tr.selection;
            const preceding = tr.doc.textBetween(Math.max(from - 1, 0), from);
            const text = preceding && !/\s/.test(preceding) ? " /" : "/";

            tr.insertText(text);

            const end = tr.selection.from;
            tr.setMeta("addToHistory", false).setMeta(VARIABLE_PICKER_TRIGGER, {
                from: end - text.length,
                slash: end - 1,
                to: end,
            });

            return true;
        })
        .run();
    if (!inserted) return false;

    /*
        The suggestion decides synchronously, in its state, whether it took the `/`. One that
        refused never opens, so it never closes either, and nothing else would take the `/` out.
     */
    const opened = pickerSessions(editor.state).find(({trigger}) => trigger);
    if (!opened?.trigger) return false;

    const {trigger} = opened;
    const taken = pickerSessions(editor.state).some(
        ({suggestion}) => suggestion?.active && suggestion.range.from === trigger.slash,
    );
    if (taken) return true;

    removeTrigger(editor, trigger.from, trigger.to);
    return false;
}

export default openVariablePicker;
